import React from 'react';
import { Table, Tag, Tabs, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import SwitchShop from '../../shop/switch-shop';
import getCurrentShop from '../../shop/get-current-shop';
import dayjs from 'dayjs';
import usePagination from '../../../components/pagination';

const statusMap = {
    PENDING: { text: '未成团', color: 'orange' },
    SUCCESS: { text: '已成团', color: 'green' },
    FAILURE: { text: '成团失败', color: 'red' },
};

const orderStatusMap = {
    1: { text: '有效', color: 'green' },
    2: { text: '已取消', color: 'default' },
};

const formatMoney = (cent) => cent ? `¥${(cent / 100).toFixed(2)}` : '';

const formatTime = (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-';

const nameText = (username, phoneNo) => {
    if (username) return `${username}${phoneNo ? `（${phoneNo}）` : ''}`;
    return phoneNo || '-';
};

const OrderTable = ({ group }) => {
    const orders = group?.order_group_buys || [];
    const columns = [
        {
            title: '商品',
            key: 'goods',
            render: (text, record) => {
                const meta = record.meta || {};
                const sku = (meta.skuList || []).find(s => String(s.skuId) === String(meta.skuId));
                return (
                    <div>
                        <div>{meta.goodsInfo?.title || '-'}</div>
                        {sku?.title && (
                            <div style={{ fontSize: '12px', color: '#999' }}>SKU：{sku.title}</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: '客户',
            key: 'customer',
            render: (text, record) => nameText(record.customer?.username, record.customer?.phoneNo),
        },
        {
            title: '数量',
            dataIndex: 'quantity',
            render: (text, record) => `${text || 0}${record.unit ? ` ${record.unit}` : ''}`,
        },
        {
            title: '参团时间',
            dataIndex: 'createTime',
            render: (text) => formatTime(text),
        },
        {
            title: '订单状态',
            dataIndex: 'status',
            render: (text) => {
                const info = orderStatusMap[text] || { text: '-', color: 'default' };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        {
            title: '关联订单',
            key: 'order',
            render: (text, record) => {
                if (!record.orderId || !record.order) return '-';
                return (
                    <a href={`/order/detail?parentOrderId=${record.order.orderId}&shopId=${group.shopId}`}>
                        {`${record.order.orderNo || ''}${record.order.realAmount ? ` · ${formatMoney(record.order.realAmount)}` : ''}`}
                    </a>
                );
            },
        },
    ];

    return (
        <Table
            rowKey="orderGroupBuyId"
            size="small"
            columns={columns}
            dataSource={orders}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无订单" /> }}
        />
    );
};

export default () => {
    const urlParams = new URLSearchParams(window.location.search);
    const groupTmpId = urlParams.get('groupTmpId');
    const [groups, setGroups] = React.useState([]);
    const [currentShopId, setCurrentShopId] = React.useState(null);
    const [status, setStatus] = React.useState('ALL');
    const [templateInfo, setTemplateInfo] = React.useState(null);
    const statusRef = React.useRef('ALL');

    const { pagination, setTotal, setCurrent } = usePagination({
        onChange: (page, pageSize) => {
            getGroups(page, pageSize);
        },
    });

    React.useEffect(() => {
        const fetchShop = async () => {
            const shop = await getCurrentShop();
            setCurrentShopId(shop?.shopId);
        };
        fetchShop();
    }, []);

    React.useEffect(() => {
        if (currentShopId) {
            if (groupTmpId) {
                getTemplateInfo();
            }
            getGroups();
        }
    }, [currentShopId]);

    const getTemplateInfo = async () => {
        const url = `/api/promotion/group-buy-template/detail?groupTmpId=${groupTmpId}&shopId=${currentShopId}`;
        const response = await fetch(url);
        const result = await response.json();
        if (result && result.success) {
            setTemplateInfo(result.data);
        }
    };

    const getGroups = async (page = 1, pageSize = 10) => {
        const st = statusRef.current;
        const url = `/api/promotion/group-buy/list?shopId=${currentShopId}&status=${st}${groupTmpId ? `&groupTmpId=${groupTmpId}` : ''}&page=${page}&pageSize=${pageSize}`;
        const response = await fetch(url);
        const result = await response.json();
        if (result && result.success) {
            setTotal(result?.data?.count || 0);
            setGroups(result?.data?.rows || []);
        }
    };

    const onStatusChange = (key) => {
        statusRef.current = key;
        setStatus(key);
        // 重置到第一页再拉取；已在第一页时直接拉取
        if (pagination.current === 1) {
            getGroups(1, pagination.pageSize);
        } else {
            setCurrent(1);
        }
    };

    const columns = [
        {
            title: '团购名称',
            key: 'title',
            render: (text, record) => (
                <div>
                    {record.title}
                    {record.groupTmp?.title && (
                        <div style={{ fontSize: '12px', color: '#999' }}>{record.groupTmp.title}</div>
                    )}
                </div>
            ),
        },
        {
            title: '团长',
            key: 'leader',
            render: (text, record) => nameText(record.leader?.username, record.leader?.phoneNo),
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (text) => {
                const info = statusMap[text] || { text: text, color: 'default' };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        {
            title: '参团人数',
            key: 'orderCount',
            render: (text, record) => (record.order_group_buys || []).filter(o => o.status === 1).length,
        },
        {
            title: '开团时间',
            dataIndex: 'createTime',
            render: (text) => formatTime(text),
        },
        {
            title: '截止时间',
            dataIndex: 'deadLine',
            render: (text) => formatTime(text),
        },
    ];

    return (
        <div>
            <SwitchShop />

            <h2 className="admin-page-title">
                {groupTmpId ? (
                    <span>
                        <a href={`/promotion/group-buy?shopId=${currentShopId}`} style={{ marginRight: '8px' }}>
                            <ArrowLeftOutlined />
                        </a>
                        查看团购{templateInfo?.title ? `：${templateInfo.title}` : ''}
                    </span>
                ) : (
                    '查看团购'
                )}
            </h2>

            <Tabs
                activeKey={status}
                onChange={onStatusChange}
                items={[
                    { key: 'ALL', label: '全部' },
                    { key: 'PENDING', label: '未成团' },
                    { key: 'SUCCESS', label: '已成团' },
                    { key: 'FAILURE', label: '成团失败' },
                ]}
            />

            <Table
                rowKey="groupId"
                dataSource={groups}
                columns={columns}
                pagination={pagination}
                scroll={{ x: 'max-content' }}
                expandable={{
                    expandedRowRender: (record) => (
                        <div className="group-buy-orders-panel">
                            <div className="group-buy-orders-panel__title">
                                订单明细
                                <span className="group-buy-orders-panel__count">
                                    {record.order_group_buys?.length || 0} 单
                                </span>
                            </div>
                            <OrderTable group={record} />
                        </div>
                    ),
                    expandedRowClassName: () => 'group-buy-expanded-row',
                    rowExpandable: (record) => (record.order_group_buys || []).length > 0,
                }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无团购" /> }}
            />
        </div>
    )
}
